import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogHeader,
  DialogBody,
} from '@chakra-ui/react'
import { FiActivity, FiClipboard, FiTarget, FiCompass, FiX, FiArrowRight } from 'react-icons/fi'
import type { IntentId, Intent } from './intents'
import { INTENTS, FIRST_RUN_INTAKE_PROMPTS } from './intents'
import { useOnboardingIntent } from './useOnboardingIntent'
import {
  setFirstRunIntakeActive,
  storePendingFirstRunIntent,
} from '../../../utils/firstRunIntake'

const INTENT_ICONS: Record<IntentId, React.ReactNode> = {
  genetics: <FiActivity size={24} />,
  'health-records': <FiClipboard size={24} />,
  goals: <FiTarget size={24} />,
  explore: <FiCompass size={24} />,
}

function IntentCard({
  intent,
  selected,
  onClick,
}: {
  intent: Intent
  selected: boolean
  onClick: () => void
}) {
  return (
    <Box
      as="button"
      onClick={onClick}
      textAlign="left"
      p={4}
      borderRadius="lg"
      border="2px"
      borderColor={selected ? 'brand.500' : 'border'}
      bg={selected ? 'brand.50' : 'bg'}
      _hover={{ borderColor: 'brand.400', bg: selected ? 'brand.50' : 'bg.subtle' }}
      transition="all 0.15s"
      w="full"
    >
      <VStack align="start" gap={2}>
        <Box color={selected ? 'brand.600' : 'fg.muted'}>
          {INTENT_ICONS[intent.id]}
        </Box>
        <Text fontWeight="600" fontSize="sm" color={selected ? 'brand.700' : 'fg'}>
          {intent.label}
        </Text>
        <Text fontSize="xs" color="fg.muted" lineHeight="short">
          {intent.description}
        </Text>
      </VStack>
    </Box>
  )
}

function DetailStep({
  intent,
  onConfirm,
  onBack,
  saving,
}: {
  intent: Intent
  onConfirm: () => void
  onBack: () => void
  saving: boolean
}) {
  return (
    <VStack align="start" gap={5}>
      <HStack gap={3}>
        <Box color="brand.600">{INTENT_ICONS[intent.id]}</Box>
        <Text fontWeight="700" fontSize="lg">{intent.label}</Text>
      </HStack>

      <VStack align="start" gap={2} pl={1}>
        {intent.bullets.map((b) => (
          <HStack key={b} align="start" gap={2}>
            <Text color="brand.500" mt="2px" flexShrink={0}>•</Text>
            <Text fontSize="sm" color="fg.subtle">{b}</Text>
          </HStack>
        ))}
      </VStack>

      {intent.loomUrl && (
        <iframe
          src={intent.loomUrl}
          style={{ width: '100%', height: 200, borderRadius: 8, border: '1px solid var(--chakra-colors-border)' }}
          title={`How to: ${intent.label}`}
          allowFullScreen
        />
      )}

      <HStack gap={3} w="full" pt={2}>
        <Button variant="ghost" size="sm" onClick={onBack} flex={1}>
          Back
        </Button>
        <Button
          colorScheme="brand"
          size="sm"
          onClick={onConfirm}
          loading={saving}
          flex={2}
        >
          Start with Coach <FiArrowRight style={{ marginLeft: 6 }} />
        </Button>
      </HStack>
    </VStack>
  )
}

interface IntentPickerModalProps {
  isOpen: boolean
  onClose: () => void
}

export const IntentPickerModal: React.FC<IntentPickerModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate()
  const { saving, pickIntent, dismiss } = useOnboardingIntent()
  const [selected, setSelected] = useState<IntentId | null>(null)
  const [step, setStep] = useState<'pick' | 'detail'>('pick')

  useEffect(() => {
    if (isOpen) {
      setSelected(null)
      setStep('pick')
    }
  }, [isOpen])

  const selectedIntent = INTENTS.find((i) => i.id === selected) ?? null

  const handleCardClick = (id: IntentId) => {
    setSelected(id)
    setStep('detail')
  }

  const handleConfirm = async () => {
    if (!selected) return
    const firstRun = FIRST_RUN_INTAKE_PROMPTS[selected]
    storePendingFirstRunIntent(firstRun)
    setFirstRunIntakeActive()
    await pickIntent(selected)
    navigate('/chat', { replace: true })
    onClose()
  }

  const handleSkip = async () => {
    await dismiss()
    onClose()
  }

  const handleClose = () => {
    setSelected(null)
    setStep('pick')
    onClose()
  }

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={(e) => { if (!e.open) handleClose() }}
      placement="center"
      size={{ base: 'sm', md: 'md' }}
    >
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent>
          <DialogHeader>
            <HStack justify="space-between" align="center">
              <VStack align="start" gap={0}>
                <Text fontWeight="700" fontSize="lg">Welcome to LiveWello</Text>
                <Text fontSize="sm" color="fg.muted">What would you like to do first?</Text>
              </VStack>
              <Button variant="ghost" size="sm" onClick={handleSkip} color="fg.muted" aria-label="Skip">
                <FiX size={16} />
              </Button>
            </HStack>
          </DialogHeader>

          <DialogBody pb={6}>
            {step === 'pick' && (
              <SimpleGrid columns={2} gap={3}>
                {INTENTS.map((intent) => (
                  <IntentCard
                    key={intent.id}
                    intent={intent}
                    selected={selected === intent.id}
                    onClick={() => handleCardClick(intent.id)}
                  />
                ))}
              </SimpleGrid>
            )}

            {step === 'detail' && selectedIntent && (
              <DetailStep
                intent={selectedIntent}
                onConfirm={handleConfirm}
                onBack={() => setStep('pick')}
                saving={saving}
              />
            )}
          </DialogBody>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
